import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  useTheme,
} from '@mui/material';
import { debounce } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { selectSearchValue, setSearchValue } from '../apiExplorerSlice';

// IMPORTANT NOTE: DO NOT COPY THIS PATTERN!  Typically for the search to work in GenericDataGrid, you can just use the SearchBarDataGrid component and MUI data grid will handle the search functionality (seeRanchSummaryTableHeader.tsx for an example).  This is a special case where because the API can be any type of data and is unknown upfront and can have objects and other complex types, we need to implement a custom search functionality.
const APIExplorerSearchBar: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const reduxSearchValue = useAppSelector(selectSearchValue);
  const [localSearchValue, setLocalSearchValue] = useState('');

  // Sync local state with Redux when component mounts
  useEffect(() => {
    setLocalSearchValue(reduxSearchValue);
  }, [reduxSearchValue]);

  // Debounce the dispatch to avoid too many Redux updates
  const debouncedDispatch = React.useMemo(
    () =>
      debounce((value: string) => {
        dispatch(setSearchValue({ newValue: value }));
      }, 300),
    [dispatch]
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setLocalSearchValue(newValue);
    debouncedDispatch(newValue);
  };

  const handleClearSearch = () => {
    setLocalSearchValue('');
    dispatch(setSearchValue({ newValue: '' }));
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '38rem',
      }}
    >
      <TextField
        fullWidth
        value={localSearchValue}
        onChange={handleSearchChange}
        placeholder='Search...'
        variant='standard'
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon color='action' />
              </InputAdornment>
            ),
            endAdornment: localSearchValue ? (
              <InputAdornment position='end'>
                <IconButton
                  aria-label='clear search'
                  onClick={handleClearSearch}
                  size='small'
                >
                  <ClearIcon fontSize='small' />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: {
              bgcolor: theme.colors.canopy.grey[25],
              border: `1px solid ${theme.colors.canopy.grey[200]}`,
              borderRadius: '0.5rem',
              p: '0.63rem 1rem 0.63rem 0.69rem',
              '&:before': {
                borderBottom: 'none',
              },
              '&:after': {
                borderColor: theme.colors.canopy.primary.main,
              },
              '& .MuiIconButton-root': {
                color: theme.colors.canopy.content.primary,
              },
            },
          },
        }}
      />
    </Box>
  );
};

export default APIExplorerSearchBar;
